/* eslint-disable no-param-reassign */
import _ from 'lodash';
import {
  AppAPIName,
  MessagesAPIName,
  UnAuthAPIName,
  UsersAPIName,
} from '../../constants';
import { ChangeModel } from '../../store/ui/types';
import { getAxiosInstance } from '../ApiUtils';
import { portalConfigNameListModel, NameListModel } from './portalModels';

export interface FeaturesConfigType {
  signupDisabled?: boolean;
  filesPageDisabled?: boolean;
  messagingPageDisabled?: boolean;
  navbarSaveEnabled?: boolean;
  resourceSectionDisabled?: boolean;
  paymentsModuleDisabled?: boolean;
  subscriptionModuleDisabled?: boolean;
  emailUpdateDisabled?: boolean;
  formsPageDisabled?: boolean;
  clientInvitesDisabled?: boolean;
}

const FeaturesConfig: FeaturesConfigType = {};

export const defaultAuthImage = '/images/auth_image.png';
export const defaultSocialImage =
  'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png?format=webp&v=1530129081';

interface setupConfigInput {
  config: any;
  apiStage?: string;
  coreApiUrl?: string;
  unAuthApiUrl?: string;
  usersApiUrl?: string;
}

export const setupPortalConfig = ({
  config,
  apiStage,
  coreApiUrl,
  unAuthApiUrl,
  usersApiUrl,
}: setupConfigInput) => {
  // do not use constants variable here to skip circular ref.
  config.features = config.features || FeaturesConfig;
  config.assets.logoStyle = config.assets.logoStyle || {
    filter: 'brightness(0) invert(1)',
    height: '35px',
  };

  config.assets.authImage = config.assets.authImage || {
    signInUrl: defaultAuthImage,
    signInStyle: {},
    signUpUrl: defaultAuthImage,
    signUpStyle: {},
  };

  config.assets.authImage.signInUrl =
    config.assets.authImage.signInUrl || defaultAuthImage;
  config.assets.authImage.signUpUrl =
    config.assets.authImage.signUpUrl || defaultAuthImage;
  config.assets.socialShareImage.imageUrl =
    config.assets.socialShareImage.imageUrl || defaultSocialImage;

  const baseEndpoint = {
    region: 'us-west-2',
    custom_header: () => {
      let portalConfig;
      try {
        portalConfig = config;
      } catch (e) {
        if (e instanceof ReferenceError) {
          // config var is not defined because this function is being on the client side
          // use window
          portalConfig = window.App.portalConfig;
        }
      }
      return {
        Portal: portalConfig.portalHeader,
      };
    },
  };

  // api endpoints are used when configuring Amplify with API.configure
  // Amplify is the client used to make api calls and handles setting signatures and headers.
  // These settings tell the Amplify client what urls to use for the api
  const coreAPIEndpoint =
    coreApiUrl ||
    `https://app-api${apiStage ? `-${apiStage}` : ''}.joinportal.com`;
  config.AWS.API.endpoints = [
    {
      ...baseEndpoint,
      name: AppAPIName,
      endpoint: coreAPIEndpoint,
    },
    {
      ...baseEndpoint,
      name: MessagesAPIName,
      endpoint: `https://chat-api${
        apiStage ? `-${apiStage}` : ''
      }.joinportal.com`,
    },
    {
      ...baseEndpoint,
      name: UnAuthAPIName,
      endpoint: unAuthApiUrl || coreAPIEndpoint,
    },
    {
      ...baseEndpoint,
      name: UsersAPIName,
      endpoint: usersApiUrl || coreAPIEndpoint,
    },
  ];

  config.BaseAPIUrl = config.AWS.API.endpoints[0].endpoint;

  config.MarketingSite = config.MarketingSite || {
    Enabled: false,
    S3Prefix: '',
  };

  config.Chat = {
    ClientId: apiStage === 'dev' ? 'uwae3u5x7eyz' : 'juz5j72pjuuu',
  };
};

export const getPortalHostFromHostname = (host: string): string => {
  if (!host) {
    return 'localhost';
  }

  const hostParts = host.split('.');
  // see if host format is like: subdomain.domain.ext
  // or subdomain.domain.co.uk
  if (hostParts.length > 2) {
    // if portal is hosted by us then the domain.net === joinportal.com
    if (host.endsWith('.joinportal.com')) {
      return hostParts[0];
    }

    // otherwise the portal is hosted on a subdomain like www, portal etc. and just want the
    // domain part in host
    return hostParts[1];
  }

  // see if host format is like: mydomain.com
  if (hostParts.length === 2) {
    return hostParts[0]; // only return the domain name with the suffix
  }

  // portal setup to use custom domain config should be
  // placed in customdomain dir
  return host.replace('.', '-');
};

export const loadPortalConfig = async (
  hostname: string,
  stage: string,
  subdomain: string,
  coreApiUrl: string,
  unAuthApiUrl: string,
  usersApiUrl: string,
) => {
  console.info('Loading portalConfig for', hostname);
  const portalHostname = getPortalHostFromHostname(hostname);
  console.info('Using portal hostname', portalHostname);

  const axiosService = getAxiosInstance({
    stage,
    apiUrl: unAuthApiUrl || coreApiUrl,
  });
  const configResponse = await axiosService.get(
    `/portal/${portalHostname}/config`,
    {
      params: { subdomain, viewMode: process.env.VIEW_MODE },
    },
  );

  const portalConfig = configResponse.data;
  const apiStage = stage === 'production' ? '' : stage;
  setupPortalConfig({
    config: portalConfig,
    apiStage,
    coreApiUrl,
    unAuthApiUrl,
    usersApiUrl,
  });
  return portalConfig;
};

export const getConfigKey = (key: string, nameList: NameListModel) => {
  const mappedNameKey = Object.keys(nameList).find(name => name === key);
  if (mappedNameKey) {
    return nameList[mappedNameKey];
  }
  return null;
};

export const MapConfigProperty = (changes: ChangeModel[], config: any) => {
  const portalConfig = _.cloneDeep(config);
  if (changes && changes.length) {
    changes.forEach(change => {
      const configKey = getConfigKey(change.name, portalConfigNameListModel);
      if (configKey) {
        _.set(
          portalConfig,
          configKey.path,
          configKey.invertBooleanValue ? !change.value : change.value,
        );
      }
    });
  }
  return portalConfig;
};
